import { useState, useEffect, useCallback } from 'react'
import { UserPlus, Users as UsersIcon, Eye, Pencil, Shield, UserX, UserCheck, Trash2, Building2, X, Monitor, Save, Loader2, ShieldAlert } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip'
import { Link, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { DataTable, type ColumnDef, useDataTable } from '@/components/ui/data-table'
import { userService, type UserOData, type UserRoleOData } from '@/lib/api/services/users'
import { useToast } from '@/components/ui/use-toast'
import { PermissionGate } from '@/components/auth/permission-gate'
import { useAuth } from '@/lib/contexts/auth-context'
import { PageHeader, FluentCard } from '@/components/layout/page-header'
import { getAvatarProps } from '@/lib/avatar-colors'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface TenantOption {
  id: string
  name: string
}
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

// Office Add-in types
const ADDIN_TYPES = [
  { id: 'Word', name: 'Word', icon: '📝' },
  { id: 'Excel', name: 'Excel', icon: '📊' },
  { id: 'PowerPoint', name: 'PowerPoint', icon: '📽️' },
  { id: 'Outlook', name: 'Outlook', icon: '📧' },
] as const

interface UserAddinAccess {
  word: boolean
  excel: boolean
  powerPoint: boolean
  outlook: boolean
}

export default function Users() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tenantId = searchParams.get('tenantId')
  const [tenantName, setTenantName] = useState<string | null>(null)
  const { user: currentUser, hasPermission, hasAnyRole, isLoading: authLoading } = useAuth()

  // Check if user has admin access (super admin, tenant admin, or user management permission)
  const canAccessPage = currentUser?.isSuperAdmin || hasAnyRole(['Admin', 'Tenant Admin']) || hasPermission('user.view')

  const [users, setUsers] = useState<UserOData[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { toast } = useToast()

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Show access denied if user doesn't have permission
  if (!canAccessPage) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="rounded-full bg-destructive/10 p-4 mb-4">
          <ShieldAlert className="h-12 w-12 text-destructive" />
        </div>
        <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
        <p className="text-muted-foreground max-w-md mb-6">
          You don't have permission to access the User Management page. Please contact your administrator if you need access.
        </p>
        <Button variant="outline" onClick={() => window.history.back()}>
          Go Back
        </Button>
      </div>
    )
  }

  // Company filter state for admins
  const [availableCompanies, setAvailableCompanies] = useState<TenantOption[]>([])
  const [companiesLoading, setCompaniesLoading] = useState(false)

  // Modal states
  const [selectedUser, setSelectedUser] = useState<UserOData | null>(null)
  const [viewModalOpen, setViewModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [rolesModalOpen, setRolesModalOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [addinModalOpen, setAddinModalOpen] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)

  // Edit form state
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
  })

  // Roles state
  const [availableRoles, setAvailableRoles] = useState<UserRoleOData[]>([])
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([])

  // Add-in access state (per user)
  const [userAddinAccess, setUserAddinAccess] = useState<UserAddinAccess>({
    word: true,
    excel: true,
    powerPoint: true,
    outlook: true,
  })

  // Check if current user is admin/superadmin
  const isAdmin = currentUser?.isSuperAdmin || currentUser?.roles?.some(r =>
    r.name === 'Admin' || r.name === 'SuperAdmin' || r.name === 'System Admin'
  )

  // Clear tenant filter
  const clearTenantFilter = () => {
    searchParams.delete('tenantId')
    searchParams.delete('userId')
    setSearchParams(searchParams)
    setTenantName(null)
  }

  // Handle company filter change
  const handleCompanyChange = (value: string) => {
    if (value === 'all') {
      searchParams.delete('tenantId')
    } else {
      searchParams.set('tenantId', value)
    }
    setSearchParams(searchParams)
  }

  const {
    page,
    pageSize,
    sortField,
    sortDirection,
    filters,
    search,
    setPage,
    setPageSize,
    setSort,
    setFilters,
    setSearch,
    getODataParams,
  } = useDataTable({
    initialState: {
      pageSize: 20,
      sortField: 'email',
      sortDirection: 'asc',
    },
  })

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const params = getODataParams()

      // Add roles expansion
      params.expand = ['roles']

      // Add tenant filter if tenantId is provided
      if (tenantId) {
        const tenantFilter = { field: 'tenantId', operator: 'eq' as const, value: tenantId }
        params.filter = params.filter
          ? [...params.filter, tenantFilter]
          : [tenantFilter]
      }

      const response = await userService.getUsers(params)
      setUsers(response.value || [])
      setTotalCount(response['@odata.count'] || response.value?.length || 0)
    } catch (_error) {
      setError('Failed to load users')
      toast({
        title: 'Error',
        description: 'Failed to load users',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }, [getODataParams, toast, tenantId])

  // Load tenant name when filtering by tenant
  useEffect(() => {
    const loadTenantName = async () => {
      if (tenantId) {
        try {
          const { tenantsService } = await import('@/lib/api/services/tenants')
          const tenant = await tenantsService.getTenant(tenantId)
          setTenantName(tenant.name)
        } catch {
          // Ignore error, just won't show tenant name
        }
      }
    }
    loadTenantName()
  }, [tenantId])

  // Load available roles
  useEffect(() => {
    const loadRoles = async () => {
      try {
        const { roleService } = await import('@/lib/api/services/roles')
        const rolesResponse = await roleService.getRoles()
        setAvailableRoles(rolesResponse.value as unknown as UserRoleOData[])
      } catch {
        // Ignore error
      }
    }
    loadRoles()
  }, [])

  // Load available companies for admin users
  useEffect(() => {
    const loadCompanies = async () => {
      if (!isAdmin) return
      try {
        setCompaniesLoading(true)
        const { tenantsService } = await import('@/lib/api/services/tenants')
        const response = await tenantsService.getTenants({ pageSize: 100, sortBy: 'name' })
        const companies = (response.items || []).map((t) => ({
          id: t.id,
          name: t.name
        }))
        setAvailableCompanies(companies)
      } catch {
        // Ignore error
      } finally {
        setCompaniesLoading(false)
      }
    }
    loadCompanies()
  }, [isAdmin])

  useEffect(() => {
    loadUsers()
  }, [loadUsers])

  // Action handlers
  const handleViewUser = (user: UserOData) => {
    setSelectedUser(user)
    setViewModalOpen(true)
  }

  const handleEditUser = (user: UserOData) => {
    setSelectedUser(user)
    setEditForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email,
    })
    setEditModalOpen(true)
  }

  const handleManageRoles = (user: UserOData) => {
    setSelectedUser(user)
    setSelectedRoleIds((user.roles || []).map(r => r.id))
    setRolesModalOpen(true)
  }

  const handleManageAddins = (user: UserOData) => {
    setSelectedUser(user)
    // In a real implementation, load user's add-in access from API
    setUserAddinAccess({
      word: true,
      excel: true,
      powerPoint: true,
      outlook: true,
    })
    setAddinModalOpen(true)
  }

  const handleDeleteUser = (user: UserOData) => {
    setSelectedUser(user)
    setDeleteDialogOpen(true)
  }

  const handleToggleStatus = async (user: UserOData) => {
    try {
      setActionLoading(true)
      if (user.isActive) {
        await userService.deactivateUser(user.id)
      } else {
        await userService.activateUser(user.id)
      }
      toast({
        title: 'Success',
        description: `User ${user.isActive ? 'deactivated' : 'activated'} successfully`,
      })
      loadUsers()
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to update user status',
        variant: 'destructive',
      })
    } finally {
      setActionLoading(false)
    }
  }

  const handleSaveEdit = async () => {
    if (!selectedUser) return
    try {
      setActionLoading(true)
      await userService.updateUser(selectedUser.id, editForm)
      toast({
        title: 'Success',
        description: 'User updated successfully',
      })
      setEditModalOpen(false)
      loadUsers()
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to update user',
        variant: 'destructive',
      })
    } finally {
      setActionLoading(false)
    }
  }

  const handleSaveRoles = async () => {
    if (!selectedUser) return
    try {
      setActionLoading(true)
      await userService.assignRoles(selectedUser.id, selectedRoleIds)
      toast({
        title: 'Success',
        description: 'User roles updated successfully',
      })
      setRolesModalOpen(false)
      loadUsers()
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to update user roles',
        variant: 'destructive',
      })
    } finally {
      setActionLoading(false)
    }
  }

  const handleSaveAddinAccess = async () => {
    if (!selectedUser) return
    try {
      setActionLoading(true)
      // In a real implementation, save to API
      // await userService.updateAddinAccess(selectedUser.id, userAddinAccess)
      toast({
        title: 'Success',
        description: 'Add-in access updated successfully',
      })
      setAddinModalOpen(false)
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to update add-in access',
        variant: 'destructive',
      })
    } finally {
      setActionLoading(false)
    }
  }

  const handleConfirmDelete = async () => {
    if (!selectedUser) return
    try {
      setActionLoading(true)
      await userService.deactivateUser(selectedUser.id)
      toast({
        title: 'Success',
        description: 'User deactivated successfully',
      })
      setDeleteDialogOpen(false)
      loadUsers()
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to delete user',
        variant: 'destructive',
      })
    } finally {
      setActionLoading(false)
    }
  }

  const columns: ColumnDef<UserOData>[] = [
    {
      id: 'email',
      header: 'User',
      accessor: 'email',
      sortable: true,
      filterable: true,
      filterType: 'text',
      cell: (user) => {
        const { initials, colorClasses } = getAvatarProps(user)
        return (
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9 rounded-full">
            <AvatarFallback className={`rounded-full font-semibold text-sm ${colorClasses}`}>{initials}</AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium text-gray-900 dark:text-white">
              {user.firstName && user.lastName
                ? `${user.firstName} ${user.lastName}`
                : user.email}
            </p>
            {user.firstName && user.lastName && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {user.email}
              </p>
            )}
          </div>
        </div>
        )
      },
    },
    {
      id: 'roles',
      header: 'Roles',
      accessor: (user) => (user.roles || []).map(r => r.name).join(', '),
      cell: (user) => (
        <div className="flex flex-wrap gap-1">
          {(user.roles || []).map((role) => (
            <Badge key={role.id} variant="secondary">
              {role.name}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      id: 'isActive',
      header: 'Status',
      accessor: 'isActive',
      sortable: true,
      filterable: true,
      filterType: 'boolean',
      cell: (user) => (
        <Badge variant={user.isActive ? 'success' : 'secondary'}>
          {user.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      id: 'createdAt',
      header: 'Joined',
      accessor: 'createdAt',
      sortable: true,
      cell: (user) => (
        <span className="text-gray-500 dark:text-gray-400">
          {new Date(user.createdAt).toLocaleDateString()}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: 'id',
      width: '220px',
      cell: (user) => (
        <TooltipProvider>
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => handleViewUser(user)}
                >
                  <Eye className="h-4 w-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>View Profile</TooltipContent>
            </Tooltip>
            <PermissionGate permissions={['users.update']}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleEditUser(user)}
                  >
                    <Pencil className="h-4 w-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Edit User</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleManageRoles(user)}
                  >
                    <Shield className="h-4 w-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Manage Roles</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleManageAddins(user)}
                  >
                    <Monitor className="h-4 w-4 text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Manage Add-in Access</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleToggleStatus(user)}
                    disabled={actionLoading}
                  >
                    {user.isActive ? (
                      <UserX className="h-4 w-4 text-orange-500 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300" />
                    ) : (
                      <UserCheck className="h-4 w-4 text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{user.isActive ? 'Deactivate' : 'Activate'}</TooltipContent>
              </Tooltip>
            </PermissionGate>
            <PermissionGate permissions={['users.delete']}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleDeleteUser(user)}
                  >
                    <Trash2 className="h-4 w-4 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Delete User</TooltipContent>
              </Tooltip>
            </PermissionGate>
          </div>
        </TooltipProvider>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title="Users"
        description={tenantId ? `Users for ${tenantName || 'selected company'}` : "Manage user accounts and permissions"}
        actions={
          <PermissionGate permissions={['users.create']}>
            <Link
              to="/admin/users/new"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-semibold rounded-full hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors shadow-lg shadow-gray-900/20 dark:shadow-white/20"
            >
              <UserPlus className="h-4 w-4" />
              Add User
            </Link>
          </PermissionGate>
        }
      />

      {/* Tenant Filter Banner - show when filtered via URL param (e.g. from company page) */}
      {tenantId && !isAdmin && (
        <Alert className="bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
          <Building2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          <AlertDescription className="flex items-center justify-between">
            <span>
              Showing users for company:{' '}
              <Link
                to={`/admin/companies/${tenantId}`}
                className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
              >
                {tenantName || tenantId}
              </Link>
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearTenantFilter}
              className="h-8 px-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
            >
              <X className="h-4 w-4 mr-1" />
              Clear Filter
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Quick Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        {[
          { label: 'Total Users', value: totalCount, color: 'from-violet-400 to-purple-600' },
          { label: 'Active', value: users.filter(u => u.isActive).length, color: 'from-emerald-400 to-green-600' },
          { label: 'Inactive', value: users.filter(u => !u.isActive).length, color: 'from-gray-400 to-gray-600' },
          { label: 'Admins', value: users.filter(u => u.roles?.some(r => r.name === 'Admin')).length, color: 'from-rose-400 to-pink-600' },
        ].map((stat) => (
          <FluentCard key={stat.label} className="flex items-center gap-4">
            <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color}`}>
              <UsersIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</p>
            </div>
          </FluentCard>
        ))}
      </div>

      <FluentCard className="overflow-hidden">
        <DataTable
          columns={columns}
          data={users}
          totalCount={totalCount}
          loading={loading}
          error={error}
          onRetry={loadUsers}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          sortField={sortField}
          sortDirection={sortDirection}
          onSortChange={setSort}
          filters={filters}
          onFilterChange={setFilters}
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search users..."
          toolbarActions={
            isAdmin && availableCompanies.length > 0 ? (
              <Select
                value={tenantId || 'all'}
                onValueChange={handleCompanyChange}
                disabled={companiesLoading}
              >
                <SelectTrigger className="w-[200px] h-10">
                  <Building2 className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="All Companies" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Companies</SelectItem>
                  {availableCompanies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : undefined
          }
          rowKey="id"
          emptyMessage="No users found"
        />
      </FluentCard>

      {/* View User Modal */}
      <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>User Profile</DialogTitle>
            <DialogDescription>
              View user details and account information.
            </DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarFallback className={`text-lg font-semibold ${getAvatarProps(selectedUser).colorClasses}`}>
                    {getAvatarProps(selectedUser).initials}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-lg font-semibold">
                    {selectedUser.firstName && selectedUser.lastName
                      ? `${selectedUser.firstName} ${selectedUser.lastName}`
                      : selectedUser.email}
                  </h3>
                  <p className="text-sm text-gray-500">{selectedUser.email}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-4">
                <div>
                  <Label className="text-gray-500">Status</Label>
                  <Badge variant={selectedUser.isActive ? 'success' : 'secondary'} className="mt-1">
                    {selectedUser.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <div>
                  <Label className="text-gray-500">Email Verified</Label>
                  <Badge variant={selectedUser.isEmailVerified ? 'success' : 'secondary'} className="mt-1">
                    {selectedUser.isEmailVerified ? 'Yes' : 'No'}
                  </Badge>
                </div>
                <div>
                  <Label className="text-gray-500">Joined</Label>
                  <p className="text-sm mt-1">{new Date(selectedUser.createdAt).toLocaleDateString()}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Last Login</Label>
                  <p className="text-sm mt-1">
                    {selectedUser.lastLoginAt
                      ? new Date(selectedUser.lastLoginAt).toLocaleDateString()
                      : 'Never'}
                  </p>
                </div>
              </div>
              <div>
                <Label className="text-gray-500">Roles</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(selectedUser.roles || []).map((role) => (
                    <Badge key={role.id} variant="secondary">{role.name}</Badge>
                  ))}
                  {(!selectedUser.roles || selectedUser.roles.length === 0) && (
                    <span className="text-sm text-gray-400">No roles assigned</span>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewModalOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user information.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                value={editForm.firstName}
                onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                value={editForm.lastName}
                onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={actionLoading}>
              {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Roles Modal */}
      <Dialog open={rolesModalOpen} onOpenChange={setRolesModalOpen}>
        <DialogContent className="sm:max-w-[550px] max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Manage Roles</DialogTitle>
            <DialogDescription>
              Assign roles to {selectedUser?.firstName || selectedUser?.email}.
            </DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="all" className="flex-1 flex flex-col min-h-0">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="all">All Roles</TabsTrigger>
              <TabsTrigger value="system">System</TabsTrigger>
              <TabsTrigger value="custom">Custom</TabsTrigger>
            </TabsList>
            <TabsContent value="all" className="flex-1 overflow-auto mt-4">
              <div className="space-y-3 pr-2">
                {availableRoles.map((role) => (
                  <div key={role.id} className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                    <Checkbox
                      id={`all-${role.id}`}
                      checked={selectedRoleIds.includes(role.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedRoleIds([...selectedRoleIds, role.id])
                        } else {
                          setSelectedRoleIds(selectedRoleIds.filter(id => id !== role.id))
                        }
                      }}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Label htmlFor={`all-${role.id}`} className="font-medium cursor-pointer">
                          {role.name}
                        </Label>
                        {role.isSystemRole && (
                          <Badge variant="outline" className="text-xs">System</Badge>
                        )}
                      </div>
                      {role.description && (
                        <p className="text-sm text-gray-500 mt-1">{role.description}</p>
                      )}
                    </div>
                  </div>
                ))}
                {availableRoles.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">No roles available</p>
                )}
              </div>
            </TabsContent>
            <TabsContent value="system" className="flex-1 overflow-auto mt-4">
              <div className="space-y-3 pr-2">
                {availableRoles.filter(r => r.isSystemRole).map((role) => (
                  <div key={role.id} className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                    <Checkbox
                      id={`system-${role.id}`}
                      checked={selectedRoleIds.includes(role.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedRoleIds([...selectedRoleIds, role.id])
                        } else {
                          setSelectedRoleIds(selectedRoleIds.filter(id => id !== role.id))
                        }
                      }}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Label htmlFor={`system-${role.id}`} className="font-medium cursor-pointer">
                          {role.name}
                        </Label>
                        <Badge variant="outline" className="text-xs">System</Badge>
                      </div>
                      {role.description && (
                        <p className="text-sm text-gray-500 mt-1">{role.description}</p>
                      )}
                    </div>
                  </div>
                ))}
                {availableRoles.filter(r => r.isSystemRole).length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">No system roles</p>
                )}
              </div>
            </TabsContent>
            <TabsContent value="custom" className="flex-1 overflow-auto mt-4">
              <div className="space-y-3 pr-2">
                {availableRoles.filter(r => !r.isSystemRole).map((role) => (
                  <div key={role.id} className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                    <Checkbox
                      id={`custom-${role.id}`}
                      checked={selectedRoleIds.includes(role.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedRoleIds([...selectedRoleIds, role.id])
                        } else {
                          setSelectedRoleIds(selectedRoleIds.filter(id => id !== role.id))
                        }
                      }}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <Label htmlFor={`custom-${role.id}`} className="font-medium cursor-pointer">
                        {role.name}
                      </Label>
                      {role.description && (
                        <p className="text-sm text-gray-500 mt-1">{role.description}</p>
                      )}
                    </div>
                  </div>
                ))}
                {availableRoles.filter(r => !r.isSystemRole).length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">No custom roles</p>
                )}
              </div>
            </TabsContent>
          </Tabs>
          <div className="pt-4 border-t mt-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                {selectedRoleIds.length} role{selectedRoleIds.length !== 1 ? 's' : ''} selected
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setRolesModalOpen(false)}>Cancel</Button>
                <Button onClick={handleSaveRoles} disabled={actionLoading}>
                  {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save Roles
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Add-in Access Modal */}
      <Dialog open={addinModalOpen} onOpenChange={setAddinModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Office Add-in Access</DialogTitle>
            <DialogDescription>
              Configure which Office add-ins {selectedUser?.firstName || selectedUser?.email} can access.
            </DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="addins" className="py-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="addins">Add-ins</TabsTrigger>
              <TabsTrigger value="features">Features</TabsTrigger>
            </TabsList>
            <TabsContent value="addins" className="space-y-4 mt-4">
              {ADDIN_TYPES.map((addin) => (
                <div key={addin.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{addin.icon}</span>
                    <div>
                      <p className="font-medium">{addin.name} Add-in</p>
                      <p className="text-sm text-gray-500">Access to {addin.name} Office add-in</p>
                    </div>
                  </div>
                  <Switch
                    checked={userAddinAccess[addin.id.toLowerCase() as keyof UserAddinAccess]}
                    onCheckedChange={(checked) =>
                      setUserAddinAccess({
                        ...userAddinAccess,
                        [addin.id.toLowerCase()]: checked
                      })
                    }
                  />
                </div>
              ))}
            </TabsContent>
            <TabsContent value="features" className="space-y-4 mt-4">
              <div className="text-sm text-gray-500 mb-4">
                Feature-level permissions for enabled add-ins.
              </div>
              <div className="space-y-3">
                {[
                  { id: 'templateInsertion', label: 'Template Insertion', description: 'Insert templates into documents' },
                  { id: 'aiAssistance', label: 'AI Assistance', description: 'Use AI-powered features' },
                  { id: 'brandCompliance', label: 'Brand Compliance', description: 'Access brand compliance tools' },
                  { id: 'dataConnectors', label: 'Data Connectors', description: 'Connect to external data sources' },
                ].map((feature) => (
                  <div key={feature.id} className="flex items-center justify-between p-2">
                    <div>
                      <p className="font-medium text-sm">{feature.label}</p>
                      <p className="text-xs text-gray-500">{feature.description}</p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddinModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveAddinAccess} disabled={actionLoading}>
              {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Save className="mr-2 h-4 w-4" />
              Save Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will deactivate the user account for{' '}
              <strong>{selectedUser?.email}</strong>. The user will no longer be able to
              access the system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={actionLoading}
            >
              {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Deactivate User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
